import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import {
  createInterviewLink,
  deactivateInterviewLink,
  emptyDraft,
  fetchAtRiskLinks,
  fetchInterviewLinks,
  updateInterviewLink,
  type InterviewLink,
  type InterviewLinkDraft,
} from '../services/interviewLinksApi';
import { AtRiskBanner } from './interviewLinks/AtRiskBanner';
import { toDraft } from './interviewLinks/linkDraft';
import { LinkForm } from './interviewLinks/LinkForm';
import { LinkList } from './interviewLinks/LinkList';
import { ScreeningLinks } from './interviewLinks/ScreeningLinks';
import { ensureMyScreeningLink, isScreeningLink } from '../services/linkedinShareApi';
import { InviteDialog } from './interviewLinks/InviteDialog';
import { useRefreshAfterLinkChange } from './interviewLinks/useRefreshAfterLinkChange';
import { useAuth } from '../../../contexts/AuthContext';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

/**
 * Strip the server-owned fields so an edit round-trips cleanly.
 *
 * Layered over emptyDraft() on purpose: links stored before a field existed have
 * no value for it, and the form would then read undefined. That is not
 * hypothetical — editing a link created before `stageId` existed crashed the form
 * on `draft.stageId.trim()` and rendered a blank page. Every future field addition
 * has the same hazard, so the defaults fill the gaps once, here.
 */

/**
 * Interview Links — the TA team's source of truth for how each role is interviewed.
 *
 * A role runs several interviews (pre-screen, personal, case, final), so the list
 * groups by role and orders each group by stage. Each link carries its own
 * interviewers, availability and rules; the candidate-facing booking page renders
 * nothing but the consequences of this config.
 */
export default function InterviewLinksView() {
  const [mode, setMode] = useState<{ kind: 'list' } | { kind: 'create' } | { kind: 'edit'; id: string }>(
    { kind: 'list' }
  );
  const { user } = useAuth();
  // A new link defaults its TA-manager (owner) to the signed-in recruiter, so the
  // list filters to their roles by default. Still editable in the form.
  const newDraft = (): InterviewLinkDraft => ({ ...emptyDraft(), taOwner: (user?.email || '').toLowerCase() });
  const [draft, setDraft] = useState<InterviewLinkDraft>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [inviting, setInviting] = useState<InterviewLink | null>(null);

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['interview-links'],
    // Wrapped: react-query would otherwise pass its QueryFunctionContext into
    // fetchInterviewLinks' optional filter argument.
    queryFn: () => fetchInterviewLinks(),
  });

  /*
    The viewer's screening link (Sofia, Oct 24 2026): made on the first visit, once per mount, so
    every TA manager has one without asking. Best-effort — a failure leaves the button below. It
    never switches a closed one back on; only "Create my screening link" does.
  */
  const me = (user?.email || '').toLowerCase();
  const ensured = useRef(false);
  // "Open Interview Links" in the InMail Library lands here with ?open=screening: the folder opens
  // and comes into view, so it cannot be missed (Sofia, Oct 24 2026).
  const [arrival] = useSearchParams();
  const [screeningOpen, setScreeningOpen] = useState(() => arrival.get('open') === 'screening');
  const screeningRef = useRef<HTMLDetailsElement>(null);
  const jumpToScreening = useRef(screeningOpen);
  useEffect(() => {
    if (!jumpToScreening.current || !data || !screeningRef.current) return;
    jumpToScreening.current = false;
    screeningRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [data]);
  const [creatingScreening, setCreatingScreening] = useState(false);
  useEffect(() => {
    if (ensured.current || !me) return;
    ensured.current = true;
    ensureMyScreeningLink()
      .then((r) => {
        if (!r.created) return;
        setScreeningOpen(true);
        void refetch();
      })
      .catch(() => {});
  }, [me, refetch]);

  async function createScreening() {
    setCreatingScreening(true);
    try {
      await ensureMyScreeningLink(true);
      setScreeningOpen(true);
      await refetch();
    } catch (err) {
      setFormError(errText(err, 'Could not create your screening link'));
    } finally {
      setCreatingScreening(false);
    }
  }

  // Best-effort warning layer: a failed check must not degrade the links view,
  // so errors are swallowed (retry: false — the proxy caches the answer anyway).
  const { data: atRisk } = useQuery({
    queryKey: ['interview-links-at-risk'],
    queryFn: fetchAtRiskLinks,
    retry: false,
  });

  // Shared with the ⚙️ LinkEditorModal on the request card — the two ways of editing a link must
  // invalidate identically, or one of them quietly leaves a stale board behind (which is exactly
  // what happened when this logic lived only here). See useRefreshAfterLinkChange.ts.
  const refreshAfterLinkChange = useRefreshAfterLinkChange();

  const errText = (err: unknown, fallback: string) =>
    err instanceof Error ? err.message : fallback;

  /** The one way into the editor — the list and the at-risk banner share it. */
  function openEditor(link: InterviewLink) {
    setDraft(toDraft(link));
    setFormError(null);
    setMode({ kind: 'edit', id: link.id });
  }

  /**
   * Arriving from somewhere that found a booking problem.
   *
   * `?link=<id>` opens that link's editor; `?posting=<id>&stage=<id>` opens the
   * create form pre-filled, which is the other half — "this stage has no
   * interview link" is the most common reason to be sent here, and landing on an
   * unfiltered list of everyone's links makes the reader do the search the
   * sender already did.
   *
   * Deliberately:
   *  - **Runs once per mount** (`applied`), so a refetch cannot yank the
   *    recruiter back into the editor after they navigated away from it.
   *  - **Falls THROUGH rather than dead-ending**: a `?link=` that no longer
   *    resolves (deleted link, stale cached board) still opens the create form
   *    when the posting travelled with it.
   *  - **Clears the params** once honoured, so a reload does not re-open a form
   *    the recruiter already closed, and the URL stops describing a state that
   *    is no longer true.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const applied = useRef(false);
  const links = data;
  // The role list below never shows the screening links; they have their own folder on top.
  const roleLinks = (data ?? []).filter((l) => !isScreeningLink(l));
  /* eslint-disable react-hooks/set-state-in-effect -- the URL IS an external system
     being synchronised in, and the links it names arrive asynchronously, so there is
     no render-time value to derive this from. It runs at most once per mount. */
  useEffect(() => {
    if (applied.current || !links) return;
    const wantedLink = searchParams.get('link')?.trim();
    const wantedPosting = searchParams.get('posting')?.trim();
    const wantedStage = searchParams.get('stage')?.trim();
    if (!wantedLink && !wantedPosting) return;
    applied.current = true;

    const match = wantedLink ? links.find((l) => l.id === wantedLink) : null;
    if (match) openEditor(match);
    else if (wantedPosting) {
      setDraft({
        ...newDraft(),
        postingId: wantedPosting,
        stageId: wantedStage || '',
      });
      setFormError(null);
      setMode({ kind: 'create' });
    }
    setSearchParams({}, { replace: true });
    // openEditor/newDraft are stable for this purpose and re-running on their
    // identity would defeat the once-per-mount guard above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links, searchParams, setSearchParams]);
  /* eslint-enable react-hooks/set-state-in-effect */

  async function save() {
    setSaving(true);
    setFormError(null);
    const editing = mode.kind === 'edit';
    try {
      if (mode.kind === 'edit') await updateInterviewLink(mode.id, draft);
      else await createInterviewLink(draft);
      // Creating a link is the TOP of the booking funnel: pair this count with `booking_confirmed`
      // in Amplitude and you get link-to-booking conversion, which is the number that says whether
      // self-booking is actually working. `meeting_type` splits 1:1 from panel, which behave
      // differently — a panel needs several calendars to line up.
      // Written as two literal calls rather than a ternary on the event name: the Usage Analytics
      // catalogue is verified by scanning for emitter call sites, and a computed name is invisible
      // to that check — which is precisely how nine names ended up catalogued with no emitter.
      const linkProps = {
        meeting_type: draft.meetingType || 'unknown',
        interviewer_count: draft.interviewers?.length ?? 0,
      };
      if (editing) void logAnalyticsEvent('interview_link_updated', linkProps);
      else void logAnalyticsEvent('interview_link_created', linkProps);
      await refetch();
      refreshAfterLinkChange();
      setMode({ kind: 'list' });
      setDraft(emptyDraft());
    } catch (err) {
      void logAnalyticsEvent('interview_link_save_failed', { editing });
      setFormError(errText(err, 'Could not save the interview link'));
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(link: InterviewLink) {
    setBusyId(link.id);
    try {
      await deactivateInterviewLink(link.id);
      void logAnalyticsEvent('interview_link_closed');
      await refetch();
      refreshAfterLinkChange();
    } catch (err) {
      void logAnalyticsEvent('interview_link_close_failed');
      setFormError(errText(err, 'Could not close the link'));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * Bring a closed link back.
   *
   * Closing was one-way: a closed link offered only Edit, and editing carried its
   * closed state along, so nothing here could undo it. Reuses the normal update
   * path rather than a new endpoint — `active` is an ordinary field, and the token
   * is untouched, so a link already sent to a candidate works again.
   */
  async function reopen(link: InterviewLink) {
    setBusyId(link.id);
    try {
      await updateInterviewLink(link.id, { ...toDraft(link), active: true });
      // Reopening is the signal that closing was a mistake, or that a link's life is longer than
      // the flow assumes. Frequent reopens are a design finding, not just a click.
      void logAnalyticsEvent('interview_link_reopened');
      await refetch();
      refreshAfterLinkChange();
    } catch (err) {
      void logAnalyticsEvent('interview_link_reopen_failed');
      setFormError(errText(err, 'Could not reopen the link'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Interview Links</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            The source of truth for each role: who interviews, how long, and when they are bookable.
            Create one link per stage — pre-screen, personal, case, final. Each link records its
            Lever posting id, and candidates receive their own unique booking link.
          </p>
        </div>
        {mode.kind === 'list' ? (
          <Button size="sm" variant="ghost" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className={isFetching ? 'mr-1.5 h-3.5 w-3.5 animate-spin' : 'mr-1.5 h-3.5 w-3.5'} />
            Refresh
          </Button>
        ) : null}
      </header>

      {formError && mode.kind === 'list' ? (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{formError}</span>
        </div>
      ) : null}

      {mode.kind !== 'list' ? (
        <LinkForm
          draft={draft}
          onChange={setDraft}
          onSubmit={() => void save()}
          onCancel={() => {
            setMode({ kind: 'list' });
            setFormError(null);
            setDraft(emptyDraft());
          }}
          existingLinks={data ?? []}
          saving={saving}
          error={formError}
          isEdit={mode.kind === 'edit'}
        />
      ) : isPending ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading interview links…
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p>{errText(error, 'Could not load interview links')}</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ScreeningLinks
            ref={screeningRef}
            links={(data ?? []).filter(isScreeningLink)}
            me={me}
            open={screeningOpen}
            creating={creatingScreening}
            busyId={busyId}
            onToggle={setScreeningOpen}
            onCreate={() => void createScreening()}
            onEdit={openEditor}
            onDeactivate={(link) => void deactivate(link)}
            onReopen={(link) => void reopen(link)}
          />
          <AtRiskBanner report={atRisk} links={roleLinks} onEdit={openEditor} />
          <LinkList
            links={roleLinks}
            busyId={busyId}
            onCreate={() => {
              setDraft(newDraft());
              setFormError(null);
              setMode({ kind: 'create' });
            }}
            onEdit={openEditor}
            onDeactivate={(link) => void deactivate(link)}
            onReopen={(link) => void reopen(link)}
            onInvite={setInviting}
          />
        </>
      )}

      {inviting ? <InviteDialog link={inviting} onClose={() => setInviting(null)} /> : null}
    </div>
  );
}
